import { useEffect, useState } from "react";

import {
  indexHtmlUrl,
  isDifferentSha,
  loadedUiSha,
  shaFromHtml,
  shouldCheckForUpdate,
} from "./versionCheck";

const CHECK_INTERVAL_MS = 5 * 60_000;

export const reloadApp = (): void => {
  window.location.reload();
};

const readRemoteSha = async (): Promise<string | null> => {
  const res = await fetch(indexHtmlUrl(), { cache: "no-store" });
  if (!res.ok) return null;
  return shaFromHtml(await res.text());
};

export const useAppUpdate = (intervalMs = CHECK_INTERVAL_MS): boolean => {
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    const loaded = loadedUiSha();
    if (!shouldCheckForUpdate(loaded)) return;

    let cancelled = false;
    let found = false;
    let inFlight = false;
    let intervalId = 0;

    const check = () => {
      if (
        cancelled ||
        found ||
        inFlight ||
        document.visibilityState !== "visible"
      ) {
        return;
      }
      inFlight = true;
      void readRemoteSha()
        .then((remote) => {
          if (cancelled || found || !isDifferentSha(loaded, remote)) return;
          found = true;
          window.clearInterval(intervalId);
          setUpdateReady(true);
        })
        .catch(() => {
          // Offline or a deploy in progress. The next check retries.
        })
        .finally(() => {
          inFlight = false;
        });
    };

    intervalId = window.setInterval(check, intervalMs);
    document.addEventListener("visibilitychange", check);
    check();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", check);
    };
  }, [intervalMs]);

  return updateReady;
};
